import type { ApiError } from './types';
import { msalInstance } from '../config/msalConfig';
import { acquireApiToken } from '../auth/tokenAcquisition';

export class HttpError extends Error {
  constructor(public status: number, public payload: ApiError | null) {
    super(payload?.detail ?? `HTTP ${status}`);
  }
}

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

/**
 * Acquire an Entra access token for the API and return it as an Authorization header.
 * Mirrors the CustomerSuccessPortal apiClient interceptor: silent acquisition against the
 * active MSAL account. Returns an empty header when signed out — the API then returns 401.
 */
async function authHeader(): Promise<Record<string, string>> {
  const account = msalInstance.getActiveAccount();
  if (!account) return {};
  // Single shared token path (src/auth/tokenAcquisition): initialize → silent → guarded
  // interactive fallback on needs-interaction (incl. timed_out). Returns null when a redirect
  // is in progress or acquisition failed; the API then returns 401 and the gate handles it.
  const token = await acquireApiToken(msalInstance, account);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const auth = await authHeader();
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...auth,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('json');
  const payload = isJson ? await res.json() : null;

  if (!res.ok) throw new HttpError(res.status, payload as ApiError | null);
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};
